import { log } from '@/log'
import {
  SalesOrderStatus,
  SupersedeStatus,
  SupersedingKind,
} from '@/models/api/commerce-sales-order'
import { ApiProduct } from '@/models/api/product'
import { CartScopeEq } from '@/models/cart/group/scope'
import ThumbnailUrl from '@/models/item/thumbnail-url'
import { BrandAppId, HostAppId } from '@/util/appid/defenitions'
import { toScope } from '@spa/components/pages/order/scope-transpiler'
import { ApplyingBundleDiscount } from '@spa/services/api/purchase'
import { OrderSource } from '@spa/services/order'
import { typeIsCreditCard } from '@spa/store/modules/orderConfirm/types'
import { CommerceEvent } from '@spa/store/modules/orderHistory/types/commerce-event'
import {
  convertPaymentMethod,
  InnerCreditCardPaymentMethod,
  InnerCvsPaymentMethod,
  InnerPaymentMethod,
} from '@spa/store/modules/orderHistory/types/convert/payment'
import { identity } from 'fp-ts/lib/function'
import { fromNullable } from 'fp-ts/lib/Option'
import _ from 'lodash'

/*
 * 主に以下の理由から、内部モジュールで定義された型定義をここで再export(re-export)している.
 * - クライアントコードからの依存を分散させず一元化する
 * - 既存の依存を変更せずに、型定義のみ再配置可能にする
 *
 * export { CreditCardPaymentMethod } from './convert/payment' という構文を使いたいが、
 * 下記issueの通り3.8以降 + babel7.12.7以降でないと動作しないため、手動でre-exportしている.
 * https://github.com/babel/babel-loader/issues/603
 *
 * ただし下記の問題もあり、どちらかというと re-export はライブラリ向けの手法かもしれない.
 * よりよい方法があれば、 re-export 以外の方法に以降しても良い.
 * https://github.com/my-color/front/pull/7277#discussion_r1188265330
 */
export type CreditCardPaymentMethod = InnerCreditCardPaymentMethod
export type CvsPaymentMethod = InnerCvsPaymentMethod
export type PaymentMethod = InnerPaymentMethod
export function paymentMethodIsCreditCard(method: PaymentMethod): method is CreditCardPaymentMethod {
  return typeIsCreditCard(method.kind)
}

export const initialOrderHistoryState: OrderHistoryState = {
  pages: [],
  perPage: 5,  // By default
  item: null,
}

export interface OrderHistoryState {
  pages: Page[],
  perPage: number,
  item: Order | null,
  error?: {
    load?: Error,
  },
}

/**
 * FIXME: Use or Create more better pagination library/model.
 */
export interface Page {
  pageNumber: number,
  items: Order[],
  isLastPage: boolean,
  error?: Error,
}

/**
 * 注文履歴
 * この注文履歴の型にフィールドを増やした場合、決済用の注文情報の型にもフィールドを増やす必要があるか、確認することが望ましい。
 * @see import('/client/spa/src/services/order.ts').PurchasePayload
 */
export interface Order {
  id: string,
  shortid: string,
  aux_string_3: string,
  /**
   * 招待情報
   * @see https://github.com/Everforth/growthpalette-pm/issues/304#issuecomment-2443116320
   */
  referral?: {
    invited_by: string
    context: {
      scope: {
        type: 'brand' | 'host',
        value: BrandAppId | HostAppId
      }
    }
    invited_at: string
  },
  name: string,
  brands: any[],
  chargeSheet: {
    delivery: any,
    order: any,
    payment: any,
    /**
     * サービス手数料は未指定の状態で注文される場合がある.
     * サービス手数料0円とサービス手数料未指定は意味が異なる点に注意.
     * @see https://github.com/my-color/front/issues/6175#issuecomment-1432647430
     */
    serviceFee?: {
      priceIncludingTax: {
        price: number
      }
    }
    tax: number,
    total: number,
  },
  commerceEvent: CommerceEvent[],
  delivery: {
    contactId: string,
    kind: string,
    contact: {
      address: {
        code: string,
        country: string,
        locality: string,
        region: string,
        street: string,
      },
      name: {
        firstName: string,
        lastName: string,
      },
      tel: string,
    },
    date: string,
    timeSlot: string,
    timeSlotLabel: string,
    /**
     * 再注文時に配送方法のパラメータとして指定する値
     */
    deliveryMethod: string,
    /**
     * 配送方法のラベル. 注文のパラメータとしては使わない
     */
    deliveryMethodLabel: string,
    /**
     * 出荷時に確定する実際の配送方法
     * 配送業者が決まる
     */
    actualDeliveryMethodLabel?: string
    /**
     * 置き配の置き場所.
     *
     * 置き配の場合のみ設定される
     * @see https://my-color.esa.io/posts/1311
     * @see https://github.com/Everforth/growthpalette-pm/issues/297
     */
    unattendedDelivery: string | null
    /**
     * 置き配の置き場所ラベル. 注文のパラメータとしては使わない
     */
    unattendedDeliveryLabel: string | null
  },
  /**
   * 事前の注文リクエスト作成時に { discount: { amount: 0 } } が指定されていないと
   * Engineレスポンスに含まれなくなってしまう点に注意.
   *
   * 単発注文のルートでは、サーバー側で常に埋め直している模様.
   *
   * @see https://www.chatwork.com/#!rid87550748-1495266408857116672
   * @see \App\Controller\Api\User\Subscription\ApplyRequest::toArray
   * @see \App\Parameter\CommercePurchase\Post::buildRequestWithDiscount
   */
  discount?: {
    amount: number,
  },
  payment: {
    method: PaymentMethod,
    point?: PaymentPoint,
  },
  paymentMethodKind: string,
  /**
   * @note 2023/04/30時点では、 コンビニ決済でのみ使用
   */
  paymentProviderAuthNumber: string,
  /**
   * @note 2023/04/30時点では、 コンビニ決済でのみ使用
   */
  paymentProviderExpireDate: string,
  salesOrderStatus: SalesOrderStatus,
  salesOrderStatusDetail: any,
  statusId: number,
  shipmentOrder?: {
    actual_delivery_method_label?: string
  },
  tags: any[],
  createdAt: Date,
  publishAt: Date,
  updatedAt: Date,
  products: Product[],
  isPreOrder: boolean
  error?: {
    join: Error,
  },
  isFreeShippingOnCatalogOnly: boolean,
  attachment?: {
    applyingBundleDiscount?: ApplyingBundleDiscount
  },
  item?: Item[],
  bundleDiscountedPrice?: number,
  campaignId?: string,
  couponId?: string,
  couponDiscountedPrice?: number,
  estimatedPoints?: number,
  pointCalculationJson?: string, // ポイント算出用JSON文字列
  supersedeStatus?: SupersedeStatus,
  supersedingKind?: SupersedingKind,
  cartScope: CartScopeEq,
  orderSource: OrderSource,
}

export function isOrderWithCvsPayment(order: Order): boolean {
  return order.paymentMethodKind === 'cvs-number'
}

export interface PaymentPoint {
  kind: 'point',
  amount: number,
}

/**
 *  Order's 'part' data along with joined ROOT/FKU/SKU data
 */
export interface Product {
  id: string,
  rootShortId: string,
  itemName?: string,
  thumbnail?: string,
  unitPrice: number,
  unitCount: number,
  totalPrice: number,
  tax: number,
  brandName?: string,
  brandEnglishName: string | null,
  skuId: string,
  fkuId: string,
  rootId?: string,
  size?: string,
  color?: string,
  productTag?: string,
  productclassTag?: string,
  alphabeticColor?: string,
  startAt?: string,
  isChild: boolean,
  isNovelty: boolean,
  isCatalogType: boolean,
}

/**
 * Orderのdata.itemの要素
 * 必要になったキーだけ記述している
 */
interface Item {
  /**
   * EngineのDBスキーマとしては必須ではないことに注意
   * @see https://github.com/Everforth/growthpalette-front/pull/7928#discussion_r1530563109
   */
  cart_id?: string,
}

export function convertToOrders(data: unknown[]): Order[] {
  return _.map(data, convertToOrder)
}

export function convertToOrder(data: unknown): Order {
  return {
    id: _.get(data, 'id'),
    shortid: _.get(data, 'shortid'),
    aux_string_3: _.get(data, 'aux_string_3'),
    referral: fromNullable(_.get(data, 'aux_string_13', null))
      .map((strReferral: string) => JSON.parse(strReferral))
      .fold(
        undefined,
        (rawReferral): Required<Order['referral']> => ({
          invited_by: _.get(rawReferral, 'invited_by'),
          context: {
            scope: {
              type: _.get(rawReferral, 'context.scope.type'),
              value: _.get(rawReferral, 'context.scope.value'),
            },
          },
          invited_at: _.get(rawReferral, 'invited_at'),
        })
      ),
    name: _.get(data, 'name'),
    brands: _.get(data, 'brands'),
    chargeSheet: {
      delivery: _.get(data, 'charge_sheet.delivery'),
      order: _.get(data, 'charge_sheet.order'),
      payment: _.get(data, 'charge_sheet.payment'),
      tax: _.get(data, 'charge_sheet.tax'),
      total: _.get(data, 'charge_sheet.total'),
      serviceFee: _.get(data, 'charge_sheet.service_fee'),
    },
    commerceEvent: _.get(data, 'commerce_event'),
    delivery: {
      contactId: _.get(data, 'delivery.contact_id'),
      kind: _.get(data, 'delivery.kind'),
      contact: {
        address: {
          code: _.get(data, 'delivery.contact.address.code'),
          country: _.get(data, 'delivery.contact.address.country'),
          locality: _.get(data, 'delivery.contact.address.locality'),
          region: _.get(data, 'delivery.contact.address.region'),
          street: _.get(data, 'delivery.contact.address.street'),
        },
        name: {
          firstName: _.get(data, 'delivery.contact.name.first_name'),
          lastName: _.get(data, 'delivery.contact.name.last_name'),
        },
        tel: _.get(data, 'delivery.contact.tel'),
      },
      date: _.get(data, 'delivery.date'),

      timeSlot: _.get(data, 'delivery.timeSlot'),
      timeSlotLabel: _.get(data, 'delivery.timeSlotLabel'),
      /**
       * 注文時に指定した配送方法を示す
       *
       * 複数配送方法対応以前は、このパラメータが存在しないため、同対応以前の注文履歴からは配送方法を取得できない。
       * しかし、同対応後は注文時に必ずこのパラメータを付与しなければならない。
       * したがって、ここでデフォルト値として「ヤマト宅急便」を設定する。
       */
      deliveryMethod: _.get(data, 'delivery.delivery_method', 'ヤマト宅急便'),
      /**
       * 注文時に指定した配送方法を示す
       *
       * delivery_method_label は
       * {@see https://github.com/Everforth/growthpalette-pm/issues/297} で追加されたフィールド.
       * #297の対応以前に作成された注文レコードでは存在しない可能性がある.
       * @see https://my-color.esa.io/posts/1311
       */
      deliveryMethodLabel: _.get(
        data,
        'delivery.delivery_method_label',
        // delivery_method_labelが存在しない = 古いレコードの場合は、
        // 旧実装のキーを参照させている.
        _.get(data, 'delivery.delivery_method', 'ヤマト宅急便')
      ),
      /**
       * 出荷時に確定した配送方法を示す
       * - 出荷前には値が存在しない
       * - 古いデータでは値が存在しない
       * - 2025/03時点で、MCとLEDIANでは値が存在しない
       * @see https://my-color.esa.io/posts/1311
       */
      actualDeliveryMethodLabel: _.get(data, 'shipment_order.actual_delivery_method_label'),
      unattendedDelivery: _.get(data, 'delivery.unattended_delivery'),
      unattendedDeliveryLabel: _.get(data, 'delivery.unattended_delivery_label'),
    },
    discount: _.get(data, 'discount'),
    payment: {
      method: convertPaymentMethod(_.get(data, 'payment.method', {})).fold(
        (errors) => {
          const errorMessages = errors.map(e => e.message).join(', ')
          log.error(errors, errorMessages, data)

          throw new Error(`could not parse payment.method: ${errorMessages}`)
        },
        identity
      ),
      point: _.get(data, 'payment.point') ?
        {
          kind: _.get(data, 'payment.point.kind'),
          amount: _.get(data, 'payment.point.amount'),
        } :
        undefined,
    },
    paymentMethodKind: _.get(data, 'payment_method_kind'),
    paymentProviderAuthNumber: _.get(data, 'payment_provider_auth_number'),
    paymentProviderExpireDate: _.get(data, 'payment_provider_expire_date'),
    salesOrderStatus: _.get(data, 'sales_order_status'),
    salesOrderStatusDetail: _.get(data, 'sales_order_status_detail'),
    shipmentOrder: _.get(data, 'shipment_order'),
    statusId: _.get(data, 'status_id'),
    tags: _.get(data, 'tags'),
    createdAt: _.get(data, 'created_at'),
    publishAt: _.get(data, 'publish_at'),
    updatedAt: _.get(data, 'update_at'),
    products: [],
    isPreOrder: _.get(data, 'tags', []).some(t => t.pathname === 'mycolor.salesorder.preorder'),
    isFreeShippingOnCatalogOnly: false,
    attachment: _.get(data, 'attachment', {}),
    item: convertToItem(_.get(data, 'item')),
    bundleDiscountedPrice: _.get(data, 'aux_long_2'),
    campaignId: _.get(data, 'aux_string_4'),
    couponId: _.get(data, 'aux_string_5'),
    couponDiscountedPrice: _.get(data, 'aux_long_3'),
    estimatedPoints: _.get(data, 'aux_long_4'),
    pointCalculationJson: _.get(data, 'aux_text_3'),
    supersedeStatus: _.get(data, 'supersede_status'),
    supersedingKind: _.get(data, 'superseding_kind'),
    cartScope: toScope(_.get(data, 'aux_string_9', '')),
    orderSource: OrderSource.createFromNullable(_.get(data, 'aux_string_14', null)),
  }
}

export function convertToProducts(data: any[]): Product[] {
  return _.map(data, convertToProduct)
}

/**
 * Convert 'part' data of raw cart response to Product type
 */
export function convertToProduct(rawPart: any): Product {
  const product = new ApiProduct(rawPart)
  const belongingBrand = product.belongingBrand
  // FIXME 型定義に合わせるため、やむなくundefinedを利用
  const thumbnail = rawPart.thumbnail ? ThumbnailUrl.asSizeXS(rawPart.thumbnail).value : undefined

  return {
    id: rawPart.id,
    rootShortId: rawPart.rootShortId,
    thumbnail,
    unitPrice: _.get(rawPart, 'unitPrice'),
    totalPrice: _.get(rawPart, 'totalPrice'),
    tax: _.get(rawPart, 'tax'),
    unitCount: _.get(rawPart, 'unitCount') as number,
    skuId: _.get(rawPart, 'product_id'),
    fkuId: _.get(rawPart, 'product_class_id'),
    itemName: _.get(rawPart, 'product_name', 'NO NAME'),
    brandName: belongingBrand ? belongingBrand.name : 'NO BRAND',
    brandEnglishName: belongingBrand ? belongingBrand.englishName : null,
    productTag: _.get(rawPart, 'product_tag'),
    productclassTag: _.get(rawPart, 'productclass_tag'),
    isChild: false,
    isNovelty: false,
    isCatalogType: false,
  }
}

function convertToItem(raw: any): Item[]|undefined {
  if (raw === undefined) {
    return undefined
  }

  if (!Array.isArray(raw)) {
    return undefined
  }

  return raw.map((rawItem) => {
    return {
      cart_id: _.get(rawItem, 'cart_id'),
    }
  })
}
